import React from "react";

// ============================================================================
// ICON
// ============================================================================

function StatIcon({ type }) {
  if (type === "questions") {
    return (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        className="h-5 w-5"
        aria-hidden="true"
      >
        <circle cx="6" cy="7" r="1.5" />
        <circle cx="6" cy="12" r="1.5" />
        <circle cx="6" cy="17" r="1.5" />
        <path
          strokeLinecap="round"
          d="M11 7h8M11 12h8M11 17h8"
        />
      </svg>
    );
  }

  if (type === "marks") {
    return (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        className="h-5 w-5"
        aria-hidden="true"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M12 3l2.8 5.7 6.2.9-4.5 4.4 1.1 6.2-5.6-3-5.6 3 1.1-6.2L3 9.6l6.2-.9L12 3z"
        />
      </svg>
    );
  }

  if (type === "sections") {
    return (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        className="h-5 w-5"
        aria-hidden="true"
      >
        <path
          strokeLinecap="round"
          d="M5 5h14M5 12h14M5 19h14"
        />
        <circle
          cx="3.5"
          cy="5"
          r="0.5"
          fill="currentColor"
        />
        <circle
          cx="3.5"
          cy="12"
          r="0.5"
          fill="currentColor"
        />
        <circle
          cx="3.5"
          cy="19"
          r="0.5"
          fill="currentColor"
        />
      </svg>
    );
  }

  if (type === "groups") {
    return (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        className="h-5 w-5"
        aria-hidden="true"
      >
        <rect
          x="4"
          y="4"
          width="6"
          height="6"
          rx="1"
        />
        <rect
          x="14"
          y="4"
          width="6"
          height="6"
          rx="1"
        />
        <rect
          x="4"
          y="14"
          width="6"
          height="6"
          rx="1"
        />
        <rect
          x="14"
          y="14"
          width="6"
          height="6"
          rx="1"
        />
      </svg>
    );
  }

  return null;
}

// ============================================================================
// STAT CARD
// ============================================================================

function StatCard({
  label,
  value,
  type,
  color = "indigo",
  suffix,
}) {
  const colors = {
    indigo: {
      icon: "bg-indigo-50 text-indigo-600",
      value: "text-indigo-700",
    },

    emerald: {
      icon: "bg-emerald-50 text-emerald-600",
      value: "text-emerald-700",
    },

    amber: {
      icon: "bg-amber-50 text-amber-600",
      value: "text-amber-700",
    },

    violet: {
      icon: "bg-violet-50 text-violet-600",
      value: "text-violet-700",
    },
  };

  const styles =
    colors[color] || colors.indigo;

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4">
      <div className="flex items-center justify-between gap-3">
        <div
          className={`flex h-9 w-9 items-center justify-center rounded-lg ${styles.icon}`}
        >
          <StatIcon type={type} />
        </div>

        {suffix && (
          <span className="text-xs text-gray-400">
            {suffix}
          </span>
        )}
      </div>

      <p className="mt-3 text-xs font-medium uppercase tracking-wide text-gray-500">
        {label}
      </p>

      <p
        className={`mt-1 text-2xl font-bold ${styles.value}`}
      >
        {value}
      </p>
    </div>
  );
}

// ============================================================================
// FORMAT NUMBER
// ============================================================================

function formatNumber(value) {
  const number = Number(value);

  if (!Number.isFinite(number)) {
    return "0";
  }

  return Number.isInteger(number)
    ? number.toLocaleString()
    : number.toLocaleString(undefined, {
        maximumFractionDigits: 2,
      });
}

// ============================================================================
// SECTION QUESTIONS
// ============================================================================

function getSectionQuestions(section) {
  return Number(
    section?.questionCount ??
      section?.selectionCount ??
      section?.numberOfQuestions ??
      section?.count ??
      0
  );
}

// ============================================================================
// SECTION MARKS
// ============================================================================

function getSectionMarks(section) {
  const questionCount =
    getSectionQuestions(section);

  const marksPerQuestion = Number(
    section?.marksPerQuestion ??
      section?.marks?.marksPerQuestion ??
      section?.markSettings?.marksPerQuestion ??
      0
  );

  const explicitMarks = Number(
    section?.totalMarks ??
      section?.marks?.totalMarks ??
      section?.markSettings?.totalMarks ??
      0
  );

  if (explicitMarks > 0) {
    return explicitMarks;
  }

  return questionCount * marksPerQuestion;
}

// ============================================================================
// SECTION GROUPS
// ============================================================================

function getSectionGroups(section) {
  const groups =
    section?.groups ??
    section?.questionGroups ??
    section?.quizGroups ??
    section?.groupList ??
    null;

  if (Array.isArray(groups)) {
    return groups;
  }

  return [];
}

// ============================================================================
// SECTION GROUP COUNT
// ============================================================================
//
// IMPORTANT:
//
// During blueprint creation, actual group objects do not exist yet.
//
// The parent component stores the requested number of complete groups
// in:
//
//     section.selectionCount
//
// Therefore selectionCount must be used as the fallback.
//
// ============================================================================

function getSectionGroupCount(section) {
  const groups = getSectionGroups(section);

  if (groups.length > 0) {
    return groups.length;
  }

  return Number(
    section?.selectionCount ?? 0
  );
}

// ============================================================================
// BLUEPRINT GROUPS
// ============================================================================

function getBlueprintGroups(blueprint) {
  const groups =
    blueprint?.groups ??
    blueprint?.questionGroups ??
    blueprint?.quizGroups ??
    blueprint?.groupList ??
    [];

  if (Array.isArray(groups)) {
    return groups;
  }

  return [];
}

// ============================================================================
// CONFIGURED GROUP COUNT
// ============================================================================
//
// Used only when actual group objects exist in a saved blueprint.
//
// For a new blueprint, sections use selectionCount.
// ============================================================================

function getConfiguredGroupCount(
  sections,
  blueprint
) {
  const sectionGroups = [];

  sections.forEach((section) => {
    sectionGroups.push(
      ...getSectionGroups(section)
    );
  });

  const blueprintGroups =
    getBlueprintGroups(blueprint);

  const allGroups = [
    ...sectionGroups,
    ...blueprintGroups,
  ];

  // No actual group objects.
  //
  // This is normal while creating a blueprint.
  // Fall back to selectionCount.
  if (allGroups.length === 0) {
    return sections.reduce(
      (total, section) =>
        total +
        getSectionGroupCount(section),
      0
    );
  }

  // Deduplicate actual groups by ID.
  const groupsWithIds =
    allGroups.filter(
      (group) =>
        group?.id != null ||
        group?._id != null
    );

  if (groupsWithIds.length > 0) {
    const uniqueIds = new Set();

    groupsWithIds.forEach((group) => {
      uniqueIds.add(
        String(
          group.id ?? group._id
        )
      );
    });

    const groupsWithoutIds =
      allGroups.length -
      groupsWithIds.length;

    return (
      uniqueIds.size +
      groupsWithoutIds
    );
  }

  return allGroups.length;
}

// ============================================================================
// COMPONENT
// ============================================================================

export default function BlueprintTotals({
  sections = [],

  totals = {},

  blueprint = {},

  /*
   * These are accepted because your parent component currently passes:
   *
   * totalQuestions={totalQuestions}
   * totalMarks={estimatedMarks}
   *
   * They are optional so the component also works elsewhere.
   */
  totalQuestions: propTotalQuestions,
  totalMarks: propTotalMarks,
  totalGroups: propTotalGroups,

  showSections = true,
  showGroups = true,
  showQuestions = true,
  showMarks = true,

  compact = false,

  label = "Blueprint Summary",
}) {
  // ========================================================================
  // CALCULATED VALUES
  // ========================================================================

  const calculatedQuestions =
    sections.reduce(
      (total, section) =>
        total +
        getSectionQuestions(section),
      0
    );

  const calculatedMarks =
    sections.reduce(
      (total, section) =>
        total +
        getSectionMarks(section),
      0
    );

  const calculatedGroups =
    getConfiguredGroupCount(
      sections,
      blueprint
    );

  // ========================================================================
  // TOTAL QUESTIONS
  // ========================================================================

  const totalQuestions = Number(
    propTotalQuestions ??
      totals.questionCount ??
      totals.questions ??
      blueprint.questionCount ??
      calculatedQuestions
  );

  // ========================================================================
  // TOTAL MARKS
  // ========================================================================

  const totalMarks = Number(
    propTotalMarks ??
      totals.totalMarks ??
      totals.marks ??
      blueprint.totalMarks ??
      calculatedMarks
  );

  // ========================================================================
  // TOTAL SECTIONS
  // ========================================================================

  const totalSections = Number(
    totals.sectionCount ??
      totals.sections ??
      blueprint.sectionCount ??
      sections.length
  );

  // ========================================================================
  // TOTAL GROUPS
  // ========================================================================

  const totalGroups = Number(
    propTotalGroups ??
      totals.groupCount ??
      totals.groups ??
      blueprint.groupCount ??
      calculatedGroups
  );

  // ========================================================================
  // CARDS
  // ========================================================================

  const cards = [
    showQuestions && {
      label: "Questions",
      value: formatNumber(
        totalQuestions
      ),
      type: "questions",
      color: "indigo",
    },

    showMarks && {
      label: "Total Marks",
      value: formatNumber(totalMarks),
      type: "marks",
      color: "emerald",
    },

    showSections && {
      label: "Sections",
      value: formatNumber(
        totalSections
      ),
      type: "sections",
      color: "amber",
    },

    showGroups && {
      label: "Groups",
      value: formatNumber(totalGroups),
      type: "groups",
      color: "violet",
    },
  ].filter(Boolean);

  // ========================================================================
  // COMPACT
  // ========================================================================

  if (compact) {
    return (
      <div className="w-full">
        <div className="mb-3">
          <h3 className="text-sm font-semibold text-gray-900">
            {label}
          </h3>
        </div>

        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          {cards.map((card) => (
            <div
              key={card.label}
              className="flex items-center gap-3 rounded-lg border border-gray-200 bg-white px-3 py-2.5"
            >
              <div
                className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${
                  card.color === "emerald"
                    ? "bg-emerald-50 text-emerald-600"
                    : card.color === "amber"
                    ? "bg-amber-50 text-amber-600"
                    : card.color === "violet"
                    ? "bg-violet-50 text-violet-600"
                    : "bg-indigo-50 text-indigo-600"
                }`}
              >
                <StatIcon
                  type={card.type}
                />
              </div>

              <div className="min-w-0">
                <p className="truncate text-[10px] font-medium uppercase tracking-wide text-gray-400">
                  {card.label}
                </p>

                <p className="text-sm font-bold text-gray-800">
                  {card.value}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ========================================================================
  // NORMAL
  // ========================================================================

  return (
    <div className="w-full">
      <div className="mb-4 flex items-center justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">
            {label}
          </h3>

          <p className="mt-1 text-xs text-gray-500">
            Current totals based on the configured
            blueprint sections and groups.
          </p>
        </div>
      </div>

      {/* ================================================================ */}
      {/* STAT CARDS */}
      {/* ================================================================ */}

      <div
        className={`grid gap-3 ${
          cards.length >= 4
            ? "grid-cols-2 lg:grid-cols-4"
            : cards.length === 3
            ? "grid-cols-1 sm:grid-cols-3"
            : "grid-cols-1 sm:grid-cols-2"
        }`}
      >
        {cards.map((card) => (
          <StatCard
            key={card.label}
            {...card}
          />
        ))}
      </div>

      {/* ================================================================ */}
      {/* SECTION BREAKDOWN */}
      {/* ================================================================ */}

      {sections.length > 0 && (
        <div className="mt-4 overflow-hidden rounded-xl border border-gray-200 bg-white">
          <div className="border-b border-gray-200 bg-gray-50 px-4 py-3">
            <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-600">
              Section Breakdown
            </h4>
          </div>

          <div className="divide-y divide-gray-100">
            {sections.map(
              (section, index) => {
                const questions =
                  getSectionQuestions(
                    section
                  );

                const marks =
                  getSectionMarks(
                    section
                  );

                const groupCount =
                  getSectionGroupCount(
                    section
                  );

                const name =
                  section?.name ||
                  section?.title ||
                  section?.label ||
                  `Section ${
                    index + 1
                  }`;

                return (
                  <div
                    key={
                      section?.id ??
                      section?._id ??
                      `section-${index}`
                    }
                    className="flex items-center gap-3 px-4 py-3"
                  >
                    <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-gray-100 text-[10px] font-bold text-gray-500">
                      {index + 1}
                    </div>

                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-gray-800">
                        {name}
                      </p>
                    </div>

                    <div className="flex shrink-0 items-center gap-4 text-right">
                      {/* QUESTIONS */}

                      {showQuestions && (
                        <div>
                          <p className="text-[10px] uppercase tracking-wide text-gray-400">
                            Questions
                          </p>

                          <p className="text-xs font-semibold text-gray-700">
                            {formatNumber(
                              questions
                            )}
                          </p>
                        </div>
                      )}

                      {/* GROUPS */}

                      {showGroups && (
                        <div>
                          <p className="text-[10px] uppercase tracking-wide text-gray-400">
                            Groups
                          </p>

                          <p className="text-xs font-semibold text-violet-700">
                            {formatNumber(
                              groupCount
                            )}
                          </p>
                        </div>
                      )}

                      {/* MARKS */}

                      {showMarks && (
                        <div className="min-w-16">
                          <p className="text-[10px] uppercase tracking-wide text-gray-400">
                            Marks
                          </p>

                          <p className="text-xs font-semibold text-gray-700">
                            {formatNumber(
                              marks
                            )}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                );
              }
            )}
          </div>
        </div>
      )}
    </div>
  );
}